import _ from 'lodash';
import React from 'react';

import { isBetaHost } from 'shared/util';
import countries from 'shared/data/countries.json';

export const DEVMODE = process.env.NODE_ENV === 'development';

const log = (type, ...args) => {
  /* eslint-disable no-console */
  if (console && console[type]) {
    console[type](...args);
  }
  /* eslint-enable no-console */
};

export const dlog = (...args) => log('log', ...args);
export const dwarn = (...args) => log('warn', ...args);
export const derror = (...args) => log('error', ...args);

// Only chunk *load* (network) failures are safe to retry. If a lazy module's
// factory throws during evaluation, webpack leaves the half-initialized module
// in its cache, so retrying re-imports the poisoned module and surfaces a
// misleading "Cannot access 'X' before initialization" (TDZ) instead of the
// real error. Reject eval errors immediately so Sentry captures the true cause.
const isChunkLoadError = error => !!error && (
  error.name === 'ChunkLoadError'
  || /loading( css)? chunk|dynamically imported module/i.test(error.message || '')
);

export function retry(fn, retriesLeft = 5, interval = 1000) {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        if (!isChunkLoadError(error) || retriesLeft <= 1) {
          reject(error);
          return;
        }

        // Passing on "reject" is the important part
        setTimeout(() => {
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

export const language = () => {
  let lang = 'en-US';

  if (navigator) {
    lang = (navigator.languages && navigator.languages[0])
      || navigator.language
      || navigator.userLanguage;
  }

  return lang;
};

export const moneyFormat = (amount, currency = 'usd', locale = null) => amount.toLocaleString(
  locale || language(),
  {
    style: 'currency',
    currency,
  },
);

export const centsFormat = (amountCents, currency, locale) => moneyFormat(amountCents / 100, currency, locale);

export const isBetaSite = () => {
  return isBetaHost(_.get(window, 'location.hostname'));
};

export const countryName = code => {
  const country = _.find(countries, { code });
  return country?.name;
};

// Proxy URL for a WoW character render. `type` is the render variant our proxy
// exposes (web/routes/wowapi/index.js): avatar (square head), inset (bust),
// main, profilemain. Returns null unless region/realm/name are all present.
export const renderUrlFor = (region, realm, name, type = 'avatar') => {
  if (!(region && realm && name)) {
    return null;
  }
  const realmSlug = _.replace(_.toLower(realm), /[^a-z0-9]/g, '');
  return `/wowapi/character/${_.toLower(region)}/${realmSlug}/${name}/image/${type}`;
};

// Page Visibility API helpers. SSR-safe: with no document (server render) we
// treat the page as visible so visibility-gated polling doesn't no-op.
export const isPageVisible = () => (
  typeof document === 'undefined' || document.visibilityState !== 'hidden'
);

// Subscribe to the tab becoming visible. Fires `callback` on each
// hidden -> visible transition; returns an unsubscribe fn. Pair with
// isPageVisible() to pause polling while hidden and catch up on focus.
export const onPageVisible = (callback) => {
  if (typeof document === 'undefined' || !document.addEventListener) {
    return _.noop;
  }
  const handler = () => {
    if (isPageVisible()) callback();
  };
  document.addEventListener('visibilitychange', handler);
  return () => document.removeEventListener('visibilitychange', handler);
};

// Mount-time snapshot: useRef only keeps its initial value, so the returned
// object is what the props were on the first render, forever. For deliberate
// create-once/mount-only effects — the effect reads `snap.foo` and can list
// the (stable) snapshot in its deps without re-firing on later prop changes.
export const useMountValue = value => React.useRef(value).current;

// Latest-value ref: reassigned every render, so an effect that intentionally
// keys on something else (a derived cache key, a mode flag) can read the
// current value through `ref.current` without depending on its identity.
export const useLatest = (value) => {
  const ref = React.useRef(value);
  ref.current = value;
  return ref;
};

export const useDebounce = (callback, delay) => {
  const timeoutRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedCallback = (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return debouncedCallback;
};
